import type { LazySelectFetchOptions } from '@/components/forms/hooks/useLazySelect';
import type { FetchQueryParams } from '@/components/paginated-datatable/types/paginatedDataTableTypes';
import type { PartnerGroupSimple } from '@/views/partnergroups/partnerGroupTypes';
import {
  type CreatePartnerGroupV1Dto,
  type PartnerGroupFilterInnerV2Dto,
  type PartnerGroupPaginatedV2Dto,
  PartnerGroupsService,
  type PartnerGroupV1Dto,
  type PatchPartnerGroupV1Dto,
} from '@/openapi';
import { eventBus } from '@/configs/eventBus';

export function usePartnerGroups() {
  const fetchPartnerGroups = async () => {
    try {
      return await PartnerGroupsService.partnerGroupControllerV1FindAllV1();
    } catch (err) {
      console.log(err);
    }
  };

  const fetchPartnerGroup = async (id: string) => {
    try {
      return await PartnerGroupsService.partnerGroupControllerV1FindOneV1(id);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchPartnerGroupSimplePaginated = async (
    options: LazySelectFetchOptions,
    areaId?: string,
  ) => {
    try {
      return await PartnerGroupsService.partnerGroupControllerV2FindAllSimplePaginatedV2(
        undefined,
        options.page,
        options.limit,
        undefined,
        options.searchString,
        areaId,
      );
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const fetchPartnerGroupsPaginated = async ({
    filter,
    limit,
    orderBy,
    page,
    searchString,
  }: FetchQueryParams<PartnerGroupFilterInnerV2Dto>): Promise<PartnerGroupPaginatedV2Dto> => {
    try {
      return await PartnerGroupsService.partnerGroupControllerV2FindAllPaginatedV2(
        orderBy,
        page,
        limit,
        filter,
        searchString,
      );
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const createPartnerGroup = async (data: CreatePartnerGroupV1Dto) => {
    try {
      await PartnerGroupsService.partnerGroupControllerV1CreateV1(data);
      eventBus.emit('create');
    } catch (err) {
      console.log(err);
    }
  };

  const updatePartnerGroup = async (id: string, data: PatchPartnerGroupV1Dto) => {
    try {
      await PartnerGroupsService.partnerGroupControllerV1UpdatePartialV1(id, data);
      eventBus.emit('update');
    } catch (err) {
      console.log(err);
    }
  };

  const deletePartnerGroup = async (id: string) => {
    try {
      await PartnerGroupsService.partnerGroupControllerV1RemoveOneV1(id);
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
    }
  };

  const deletePartnerGroups = async (ids: string[]) => {
    try {
      await PartnerGroupsService.partnerGroupControllerV1RemoveManyV1({ values: ids });
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
    }
  };

  return {
    fetchPartnerGroups,
    fetchPartnerGroup,
    fetchPartnerGroupSimplePaginated,
    fetchPartnerGroupsPaginated,
    createPartnerGroup,
    updatePartnerGroup,
    deletePartnerGroup,
    deletePartnerGroups,
  };
}

export function usePartnerGroupsConverter() {
  const convertPartnerGroupDtoToPartnerGroupSimple = (
    item: PartnerGroupV1Dto,
  ): PartnerGroupSimple => {
    const { createdAt: _createdAt, modifiedAt: _modifiedAt, partners: _partners, ...res } = item;
    return { ...res, partnerCount: item.partners ? item.partners.length : 0 };
  };

  const convertPartnerGroupDtoListToPartnerGroupSimpleList = (
    data: PartnerGroupV1Dto[],
  ): PartnerGroupSimple[] => {
    return data.map((item) => {
      return convertPartnerGroupDtoToPartnerGroupSimple(item);
    });
  };

  const convertPartnerGroupSimpleToCreatePartnerGroupDto = (
    data: PartnerGroupSimple,
  ): CreatePartnerGroupV1Dto => {
    const { id: _id, version: _version, partnerCount: _partnerCount, ...res } = data;
    return res;
  };

  const convertPartnerGroupSimpleToUpdatePartnerGroupDto = (
    data: PartnerGroupSimple,
  ): PatchPartnerGroupV1Dto => {
    const { id: _id, partnerCount: _partnerCount, ...res } = data;
    return res;
  };

  return {
    convertPartnerGroupDtoToPartnerGroupSimple,
    convertPartnerGroupDtoListToPartnerGroupSimpleList,
    convertPartnerGroupSimpleToCreatePartnerGroupDto,
    convertPartnerGroupSimpleToUpdatePartnerGroupDto,
  };
}
